import { type MutableRefObject, useEffect } from 'react';

import { AppCapability, isCapable } from '@/utils/capabilities';

const NATIVE_CONTROLS = [
  'a[href]',
  'button',
  'input',
  'select',
  'summary',
  'textarea',
  '[contenteditable]',
];

const WIDGET_ROLES = [
  'alertdialog',
  'button',
  'checkbox',
  'combobox',
  'dialog',
  'grid',
  'gridcell',
  'group',
  'link',
  'listbox',
  'menu',
  'menubar',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'radio',
  'radiogroup',
  'searchbox',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'tablist',
  'textbox',
  'tree',
  'treegrid',
  'treeitem',
];

const KEYBOARD_OWNER_SELECTOR = [
  ...NATIVE_CONTROLS,
  ...WIDGET_ROLES.map((role) => `[role="${role}"]`),
].join(', ');

const isTypedCharacter = (event: KeyboardEvent) =>
  event.key.length === 1 && event.key !== ' ';

export function useFocusComposerOnType(
  inputRef: MutableRefObject<HTMLElement | undefined>,
  enabled: boolean
) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isCapable(AppCapability.HotKeys)) return;
      if (event.defaultPrevented || event.isComposing || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!isTypedCharacter(event) && event.key !== 'Backspace') return;

      const input = inputRef.current;
      if (!input || input.closest('[aria-hidden="true"], [inert]')) return;

      const active = document.activeElement;
      if (active === input) return;
      if (
        active instanceof HTMLElement &&
        active.closest(KEYBOARD_OWNER_SELECTOR)
      )
        return;

      input.focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, inputRef]);
}
